import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { TChildren } from '../../typings/common';
import styles from './Tooltip.module.scss';

type Placement = 'top' | 'bottom';
const VIEWPORT_MARGIN = 16;
const ANCHOR_GAP = 12;
const AUTO_HIDE_DELAY = 3000;

export interface Props {
  open?: boolean;
  title: string | React.ReactNode;
  children: TChildren;
  className?: string;
}

const Tooltip: React.FC<Props> = ({ open, title, children, className }) => {
  const [isVisible, setIsVisible] = useState(open ?? false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    arrowLeft: 0,
    placement: 'top' as Placement,
  });
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof open === 'boolean') {
      setIsVisible(open);
    }
  }, [open]);

  const handleClearTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [timeoutRef]);

  const handleAutoHide = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setIsVisible(false);
    }, AUTO_HIDE_DELAY);
  }, [timeoutRef]);

  const hideTooltip = useCallback(() => {
    handleClearTimeout();
    setIsVisible(false);
  }, [handleClearTimeout]);

  const handleMouseEnter = useCallback(() => {
    handleClearTimeout();
    setIsVisible(true);
  }, [handleClearTimeout]);

  const handleMouseLeave = useCallback(() => {
    handleAutoHide();
  }, [handleAutoHide]);

  const handleClick = useCallback(() => {
    setIsVisible(true);
    handleAutoHide();
  }, [handleAutoHide]);

  const calculatePosition = useCallback(() => {
    if (!isVisible || !anchorRef.current || !tooltipRef.current) {
      return;
    }

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const centerX = anchorRect.left + anchorRect.width / 2;
    const viewportWidth = window.innerWidth;
    let top = anchorRect.top - tooltipRect.height - ANCHOR_GAP;
    let placement: Placement = 'top';

    if (top < VIEWPORT_MARGIN) {
      top = anchorRect.bottom + ANCHOR_GAP;
      placement = 'bottom';
    }

    let left = centerX - tooltipRect.width / 2;
    left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(left, viewportWidth - tooltipRect.width - VIEWPORT_MARGIN)
    );

    const arrowLeft = centerX - left;

    setCoords({
      top,
      left,
      arrowLeft,
      placement,
    });
  }, [isVisible, anchorRef, tooltipRef]);

  useLayoutEffect(() => {
    calculatePosition();
  }, [calculatePosition, title]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const handleScroll = () => hideTooltip();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hideTooltip, isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('resize', calculatePosition);
    };
  }, [calculatePosition, isVisible]);

  return (
    <>
      <div
        ref={anchorRef}
        className={classNames(styles.anchor, className)}
        role='button'
        tabIndex={0}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}>
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={styles.tooltip}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-placement={coords.placement}
            style={
              {
                top: coords.top,
                left: coords.left,
                '--tooltip-arrow-left': `${coords.arrowLeft}px`,
              } as React.CSSProperties
            }>
            {title}
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
